import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, Maximize2, Minimize2, KeyRound, Monitor, StickyNote, Clock, Play, Pause, RotateCcw, ChevronUp, ChevronDown, Projector, Sparkles, Eye, Trash2, Users, Shuffle, Edit2, ShieldCheck, Repeat, Target, List, Radio, Smartphone, Rocket, Film, CornerUpLeft } from 'lucide-react';
import axios from 'axios';
import PageShell from './PageShell';
import { RVP_LIST, RVP_CONTENT } from '../data/rendezvousPedagogiques';
import { SLIDE_REGISTRY } from '../components/RvpSlides';
import ParticipantsSetupModal from '../components/ParticipantsSetupModal';
import CrossDeviceCastModal from '../components/CrossDeviceCastModal';
import SpectateurQrModal from '../components/SpectateurQrModal';
import RvpLaunchWizard from '../components/RvpLaunchWizard';
import { useParticipants, getElevePrenoms, AVATAR_EMOJIS, newId } from '../lib/rvpParticipants';
import { api } from '../lib/api';
import { useRvpCastHost } from '../lib/rvpCast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(sec) {
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

function genSessionId() {
  // Short code 6 chars A-Z0-9 (no confusing chars)
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

// Slugs des RVP qui bénéficient de la personnalisation participants
const PERSONNALISABLE_SLUGS = new Set(['aac-rvp-1', 'aac-rvp-2', 'am-synthese']);

export default function RvpPresentationPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const proMode = searchParams.get('mode') === 'pro';
  const rvp = useMemo(() => RVP_LIST.find((r) => r.slug === slug), [slug]);
  const rawSlides = useMemo(() => RVP_CONTENT[slug] || [], [slug]);
  const supportsParticipants = PERSONNALISABLE_SLUGS.has(slug);

  // Participants (localStorage per slug)
  const { data: participants, update: updateParticipants, reset: resetParticipants } = useParticipants(slug);
  const participantsRef = useRef(participants);
  useEffect(() => { participantsRef.current = participants; }, [participants]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [tirageResult, setTirageResult] = useState(null); // {prenom, ts}

  // Ouverture auto du modal la 1re fois qu'un formateur arrive sur un RVP personnalisable
  useEffect(() => {
    if (!supportsParticipants) return;
    const dismissed = sessionStorage.getItem(`rvp-participants-dismissed-${slug}`);
    if (participants.eleves.length === 0 && !dismissed) {
      setShowParticipantsModal(true);
    }
  }, [supportsParticipants, slug, participants.eleves.length]);

  // Slides finales — injection bienvenue au début + merci à la fin
  const slides = useMemo(() => {
    if (!supportsParticipants || participants.eleves.length === 0) return rawSlides;
    const bienvenue = {
      type: 'bienvenue-perso',
      title: rvp?.title ? `Bienvenue · ${rvp.title}` : 'Bienvenue',
      subtitle: 'Prêts pour un moment d\'échange et de partage ensemble ?',
      eleves: participants.eleves,
    };
    const merci = {
      type: 'merci-perso',
      subtitle: 'À très vite pour la suite de la formation. Roulez prudemment !',
      next_action: 'À la prochaine séance',
      eleves: participants.eleves,
    };
    return [bienvenue, ...rawSlides, merci];
  }, [supportsParticipants, participants.eleves, rawSlides, rvp]);

  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showJumpMenu, setShowJumpMenu] = useState(false);
  const [showVideoPanel, setShowVideoPanel] = useState(false);
  const [videoReturnIdx, setVideoReturnIdx] = useState(null); // slide où reprendre le cours après une vidéo
  const [showNotes, setShowNotes] = useState(!proMode);
  // Mode d'affichage des notes : 'side' (colonne droite) | 'focus' (grand écran centré, slide en mini)
  const [notesMode, setNotesMode] = useState(() => localStorage.getItem('rvp-notes-mode') || 'side');
  useEffect(() => { localStorage.setItem('rvp-notes-mode', notesMode); }, [notesMode]);
  const [publicWin, setPublicWin] = useState(null);
  const [showProOnboard, setShowProOnboard] = useState(proMode);
  const [revealMap, setRevealMap] = useState({}); // { qIndex: true } — per-slide reveal state
  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem(`rvp-session-${slug}`);
    if (stored) return stored;
    const s = genSessionId();
    sessionStorage.setItem(`rvp-session-${slug}`, s);
    return s;
  });
  const bcRef = useRef(null);

  // Cross-device cast (téléphone → écran distant)
  const cast = useRvpCastHost(slug);
  const [showCastModal, setShowCastModal] = useState(false);
  const [castStarting, setCastStarting] = useState(false);
  const [textZoom, setTextZoom] = useState(1);
  const [castError, setCastError] = useState('');

  // Spectateur QR — session INDÉPENDANTE dédiée aux élèves (lecture seule sur leur téléphone)
  const spectateurCast = useRvpCastHost(slug);
  const [showSpectateurModal, setShowSpectateurModal] = useState(false);
  const [spectateurStarting, setSpectateurStarting] = useState(false);
  const [spectateurError, setSpectateurError] = useState('');

  // Modal d'aide "Inverser les écrans" (affiché si moveTo échoue silencieusement)
  const [showSwapHelp, setShowSwapHelp] = useState(false);

  // Wizard "Lancer en 1 clic" — s'affiche au premier montage de la session sauf si dismissed
  const [showLaunchWizard, setShowLaunchWizard] = useState(false);

  // Ouverture auto du Launch Wizard une fois que le modal Participants est traité,
  // sauf si l'enseignant a coché "Ne plus demander" pour ce slug.
  useEffect(() => {
    if (showParticipantsModal) return;
    const skip = localStorage.getItem(`rvp-skip-launch-wizard-${slug}`);
    if (skip) return;
    const shownThisSession = sessionStorage.getItem(`rvp-launch-wizard-shown-${slug}`);
    if (shownThisSession) return;
    if (publicWin && !publicWin.closed) return;
    const t = setTimeout(() => {
      setShowLaunchWizard(true);
      sessionStorage.setItem(`rvp-launch-wizard-shown-${slug}`, '1');
    }, 300);
    return () => clearTimeout(t);
  }, [showParticipantsModal, slug, publicWin]);

  // Chrono
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const total = slides.length;
  const goPrev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);
  const goNext = useCallback(() => setCurrent((c) => Math.min(total - 1, c + 1)), [total]);
  const goExit = useCallback(() => {
    try { publicWin?.close?.(); } catch { /* noop */ }
    nav('/enseignant/rendez-vous-pedagogiques');
  }, [nav, publicWin]);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // BroadcastChannel: sync avec la fenêtre vitrine
  useEffect(() => {
    const bc = new BroadcastChannel(`rvp-sync-${slug}`);
    bcRef.current = bc;
    bc.onmessage = (e) => {
      if (e.data?.type === 'ready') {
        bc.postMessage({ slide: current, revealMap, participants, textZoom, spectateurCode: spectateurCast.active ? spectateurCast.code : null });
      }
    };
    return () => bc.close();
  }, [slug]);

  useEffect(() => {
    bcRef.current?.postMessage({ slide: current, revealMap, participants, textZoom, spectateurCode: spectateurCast.active ? spectateurCast.code : null });
  }, [current, revealMap, participants, textZoom, spectateurCast.active, spectateurCast.code]);

  // Cross-device : push l'état au serveur à chaque changement quand la session est active
  useEffect(() => {
    if (!cast.active) return;
    cast.push({ current_slide: current, reveal_map: revealMap, participants, text_zoom: textZoom, poll_session_id: sessionId });
  }, [cast, current, revealMap, participants, textZoom, sessionId]);

  // Inscription QR élèves : fusionne automatiquement les prénoms scannés dans les participants
  useEffect(() => {
    const codes = [cast.active ? cast.code : null, spectateurCast.active ? spectateurCast.code : null].filter(Boolean);
    if (codes.length === 0) return undefined;
    const iv = setInterval(async () => {
      try {
        const results = await Promise.all(codes.map((c) => api.castGet(c).catch(() => null)));
        const joined = results.flatMap((r) => r?.joined_eleves || []);
        if (joined.length === 0) return;
        const prev = participantsRef.current || { eleves: [] };
        const existing = new Set(prev.eleves.map((e) => `${(e.prenom || '').trim().toLowerCase()}|${(e.nom || '').trim().toLowerCase()}`));
        const add = [];
        joined.forEach((j) => {
          const p = (j.prenom || '').trim();
          if (!p) return;
          const key = `${p.toLowerCase()}|${(j.nom || '').trim().toLowerCase()}`;
          if (existing.has(key)) return;
          existing.add(key);
          add.push({ id: newId(), prenom: p, nom: (j.nom || '').trim(), emoji: AVATAR_EMOJIS[existing.size % AVATAR_EMOJIS.length], parents: [] });
        });
        if (add.length > 0) updateParticipants({ eleves: [...prev.eleves, ...add] });
      } catch { /* noop */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [cast.active, cast.code, spectateurCast.active, spectateurCast.code, updateParticipants]);

  // Spectateur QR : même sync, session parallèle
  useEffect(() => {
    if (!spectateurCast.active) return;
    spectateurCast.push({ current_slide: current, reveal_map: revealMap, participants, poll_session_id: sessionId });
  }, [spectateurCast, current, revealMap, participants, sessionId]);

  const startCast = useCallback(async () => {
    setCastStarting(true);
    setCastError('');
    const c = await cast.start();
    if (!c) setCastError(cast.error || 'Impossible de démarrer');
    else {
      cast.push({ current_slide: current, reveal_map: revealMap, participants, poll_session_id: sessionId });
    }
    setCastStarting(false);
  }, [cast, current, revealMap, participants, sessionId]);

  const stopCast = useCallback(async () => {
    await cast.close();
    setShowCastModal(false);
  }, [cast]);

  const startSpectateur = useCallback(async () => {
    setSpectateurStarting(true);
    setSpectateurError('');
    const c = await spectateurCast.start();
    if (!c) setSpectateurError(spectateurCast.error || 'Impossible de démarrer');
    else {
      spectateurCast.push({ current_slide: current, reveal_map: revealMap, participants, poll_session_id: sessionId });
    }
    setSpectateurStarting(false);
  }, [spectateurCast, current, revealMap, participants, sessionId]);

  const stopSpectateur = useCallback(async () => {
    await spectateurCast.close();
    setShowSpectateurModal(false);
  }, [spectateurCast]);

  const toggleReveal = useCallback(() => {
    setRevealMap((m) => ({ ...m, [current]: !m[current] }));
  }, [current]);

  const resetVotes = useCallback(async () => {
    if (!sessionId) return;
    if (!window.confirm('Effacer TOUS les votes de cette session ? (Irréversible)')) return;
    try {
      await axios.delete(`${API}/poll/reset/${sessionId}`);
    } catch { /* silent */ }
  }, [sessionId]);

  // Détecte un écran secondaire (HDMI/projecteur) via Window Management API
  const detectSecondaryScreen = async () => {
    try {
      if (typeof window.getScreenDetails !== 'function') return null;
      const details = await window.getScreenDetails();
      const screens = details.screens || [];
      if (screens.length < 2) return null;
      // Prend le premier écran qui n'est pas l'écran interne / celui du curseur
      const current = details.currentScreen;
      const secondary = screens.find((s) => s !== current) || screens.find((s) => !s.isInternal) || screens[1];
      return secondary || null;
    } catch {
      return null;
    }
  };

  const openPublicWindow = async () => {
    try {
      const url = `/enseignant/rendez-vous-pedagogiques/${slug}/vitrine?session=${sessionId}`;
      // 1. Tenter de détecter l'écran secondaire (HDMI/projecteur)
      const secondary = await detectSecondaryScreen();
      let features;
      if (secondary) {
        const left = secondary.availLeft ?? secondary.left ?? 0;
        const top = secondary.availTop ?? secondary.top ?? 0;
        const w = secondary.availWidth ?? secondary.width ?? 1280;
        const h = secondary.availHeight ?? secondary.height ?? 800;
        features = `left=${left},top=${top},width=${w},height=${h},menubar=no,toolbar=no,location=no,fullscreen=yes`;
      } else {
        // Fallback : ouvrir à droite de la fenêtre présentateur pour éviter la superposition parfaite
        const left = (window.screenX || 0) + (window.outerWidth || 800) - 20;
        features = `left=${left},top=0,width=1280,height=800,menubar=no,toolbar=no,location=no`;
      }
      const win = window.open(url, `rvp-public-${slug}`, features);
      if (!win) {
        alert("Impossible d'ouvrir la fenêtre vitrine. Autorise les popups pour ce site.");
        return;
      }
      setPublicWin(win);
      // 2. Si écran secondaire détecté, envoyer un ordre de plein écran après chargement
      if (secondary) {
        setTimeout(() => {
          try { bcRef.current?.postMessage({ cmd: 'goFullscreen' }); } catch { /* noop */ }
        }, 800);
      }
    } catch (e) {
      alert("Impossible d'ouvrir la fenêtre vitrine. Autorise les popups pour ce site.");
    }
  };

  // Bouton "Envoyer sur écran 2 & plein écran" — utile si la vitrine est déjà ouverte
  const sendToProjector = useCallback(async () => {
    try {
      if (!publicWin || publicWin.closed) {
        await openPublicWindow();
        return;
      }
      const secondary = await detectSecondaryScreen();
      if (secondary && publicWin.moveTo && publicWin.resizeTo) {
        const left = secondary.availLeft ?? secondary.left ?? 0;
        const top = secondary.availTop ?? secondary.top ?? 0;
        const w = secondary.availWidth ?? secondary.width ?? 1280;
        const h = secondary.availHeight ?? secondary.height ?? 800;
        try { publicWin.moveTo(left, top); } catch { /* noop */ }
        try { publicWin.resizeTo(w, h); } catch { /* noop */ }
      }
      try { bcRef.current?.postMessage({ cmd: 'goFullscreen' }); } catch { /* noop */ }
    } catch { /* noop */ }
  }, [publicWin]);

  // Inverser les écrans : déplace la vitrine sur l'AUTRE écran que celui où elle est actuellement.
  // Les navigateurs modernes bloquent souvent moveTo() silencieusement — on détecte l'échec
  // et on ouvre un modal d'aide avec instructions manuelles (Windows + Maj + ← / →).
  const swapScreens = useCallback(async () => {
    if (!publicWin || publicWin.closed) {
      alert("La vitrine n'est pas ouverte. Clique d'abord sur « Mode vidéoprojecteur ».");
      return;
    }
    const initialX = publicWin.screenX || 0;
    const initialY = publicWin.screenY || 0;
    try {
      // 1. Demander à la vitrine de sortir du plein écran (sinon on ne peut pas la déplacer)
      try { bcRef.current?.postMessage({ cmd: 'exitFullscreen' }); } catch { /* noop */ }
      await new Promise((r) => setTimeout(r, 450));

      if (typeof window.getScreenDetails === 'function') {
        const details = await window.getScreenDetails();
        const screens = details.screens || [];
        if (screens.length < 2) {
          setShowSwapHelp(true); // Un seul écran → montre l'aide plutôt que juste un alert
          return;
        }
        const vx = publicWin.screenX || 0;
        const currentScreen = screens.find((s) => {
          const l = s.availLeft ?? s.left ?? 0;
          const w = s.availWidth ?? s.width ?? 0;
          return vx >= l && vx < l + w;
        }) || screens[0];
        const target = screens.find((s) => s !== currentScreen) || screens[1];
        const left = target.availLeft ?? target.left ?? 0;
        const top = target.availTop ?? target.top ?? 0;
        const w = target.availWidth ?? target.width ?? 1280;
        const h = target.availHeight ?? target.height ?? 800;
        try { publicWin.moveTo(left, top); } catch { /* noop */ }
        try { publicWin.resizeTo(w, h); } catch { /* noop */ }
      } else {
        const currentX = publicWin.screenX || 0;
        const primaryW = window.screen.availWidth || 1920;
        const newX = currentX < primaryW ? primaryW : 0;
        try { publicWin.moveTo(newX, 0); } catch { /* noop */ }
      }

      // 2. Remettre en plein écran après le déplacement
      setTimeout(() => {
        try { bcRef.current?.postMessage({ cmd: 'goFullscreen' }); } catch { /* noop */ }
      }, 450);

      // 3. Détection d'échec silencieux : si après 900ms la fenêtre n'a pas bougé, montrer l'aide
      setTimeout(() => {
        try {
          const dx = Math.abs((publicWin.screenX || 0) - initialX);
          const dy = Math.abs((publicWin.screenY || 0) - initialY);
          if (dx < 100 && dy < 100 && !publicWin.closed) {
            setShowSwapHelp(true);
          }
        } catch { /* noop */ }
      }, 900);
    } catch (e) {
      setShowSwapHelp(true);
    }
  }, [publicWin]);

  // Fermer la vitrine et en rouvrir une nouvelle — contournement si moveTo est bloqué
  const reopenPublicWindow = useCallback(async () => {
    try { publicWin?.close?.(); } catch { /* noop */ }
    setPublicWin(null);
    setShowSwapHelp(false);
    await new Promise((r) => setTimeout(r, 400));
    openPublicWindow();
  }, [publicWin]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); goPrev(); }
      else if (e.key === 'Escape') { if (isFullscreen) toggleFullscreen(); else goExit(); }
      else if (e.key === 'f' || e.key === 'F') { toggleFullscreen(); }
      else if (e.key === 'n' || e.key === 'N') { setShowNotes((s) => !s); }
      else if (e.key === 'p' || e.key === 'P') { openPublicWindow(); }
      else if (e.key === 't' || e.key === 'T') { setRunning((r) => !r); }
      else if (e.key === 'r' || e.key === 'R') {
        if (slides[current]?.type === 'qcm') { e.preventDefault(); toggleReveal(); }
      }
      else if (e.key === '?') { setShowHelp((s) => !s); }
      else if (e.key === 'g' || e.key === 'G') { setShowJumpMenu((s) => !s); }
      else if (e.key === 'v' || e.key === 'V') { setShowVideoPanel((s) => !s); }
      else if (e.key === 'Home') { setCurrent(0); }
      else if (e.key === 'End') { setCurrent(total - 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, goExit, toggleFullscreen, isFullscreen, total, slides, current, toggleReveal]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Repères de navigation pour le menu « Aller à… » (sections + vidéos + titres)
  const jumpMarkers = useMemo(() => {
    return slides.map((s, i) => {
      if (s?.type === 'section') return { i, label: `${s.number || '§'} · ${s.title}`, kind: 'section' };
      if (s?.type === 'title')   return { i, label: `▶ ${s.title}`, kind: 'title' };
      if (s?.type === 'video')   return { i, label: `🎬 ${s.title || 'Vidéo'}`, kind: 'video' };
      return null;
    }).filter(Boolean);
  }, [slides]);

  // Bibliothèque vidéo — lancer une vidéo à tout moment pendant le cours
  const videoMarkers = useMemo(() => {
    return slides.map((s, i) => (s?.type === 'video' ? { i, title: s.title || 'Vidéo', note: s.note || '' } : null)).filter(Boolean);
  }, [slides]);

  const launchVideo = useCallback((videoIdx) => {
    setVideoReturnIdx((prev) => (slides[current]?.type === 'video' ? prev : current));
    setCurrent(videoIdx);
    setShowVideoPanel(false);
  }, [current, slides]);

  // Si on quitte les slides vidéo manuellement, on oublie le point de retour
  useEffect(() => {
    if (videoReturnIdx !== null && slides[current]?.type !== 'video') setVideoReturnIdx(null);
  }, [current, slides, videoReturnIdx]);

  if (!rvp) {
    return <PageShell><div className="max-w-3xl mx-auto p-16 text-white text-center">Rendez-vous pédagogique introuvable. <Link className="text-red-400 underline" to="/enseignant/rendez-vous-pedagogiques">Retour</Link></div></PageShell>;
  }

  const slide = slides[current];
  const nextSlide = slides[current + 1];
  const SlideComponent = slide ? SLIDE_REGISTRY[slide.type] : null;
  const NextComponent = nextSlide ? SLIDE_REGISTRY[nextSlide.type] : null;
  const progress = total > 0 ? ((current + 1) / total) * 100 : 0;
  const publicOpen = publicWin && !publicWin.closed;
  const pollContext = { sessionId, slug, qIndex: current };
  const isQcmSlide = slide?.type === 'qcm';
  const currentRevealed = !!revealMap[current];

  // Type de formation officielle (pour badge sur les slides obligatoires)
  const formationType = supportsParticipants
    ? (slug === 'am-synthese' ? 'AM' : (slug?.startsWith('aac-') ? 'AAC' : null))
    : null;
  const isOfficialSlide = slide && slide.type !== 'bienvenue-perso' && slide.type !== 'merci-perso';

  return (
    <div className="fixed inset-0 bg-[#0a0a0b] z-50 flex flex-col" data-testid="rvp-presenter">
      {/* Progress bar */}
      <div className="h-1 bg-white/5 relative">
        <div className="absolute inset-y-0 left-0 bg-red-500 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      {/* Top bar */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/5 gap-3 flex-wrap">
        <button onClick={goExit} data-testid="rvp-exit" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[12.5px] font-mono-spec">
          <X size={15} /> Quitter
        </button>
        <div className="text-white/40 text-[11.5px] font-mono-spec uppercase tracking-wider">
          {rvp.code} · <span className="text-white/70">{current + 1} / {total}</span> · <span className="text-emerald-300">session {sessionId}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Participants + Tirage au sort — visible uniquement sur RVP personnalisables */}
          {supportsParticipants && (
            <>
              {participants.eleves.length > 0 && (
                <button
                  onClick={() => {
                    const prenoms = getElevePrenoms(participants);
                    if (prenoms.length === 0) return;
                    const pick = prenoms[Math.floor(Math.random() * prenoms.length)];
                    setTirageResult({ prenom: pick, ts: Date.now() });
                    setTimeout(() => setTirageResult(null), 4500);
                  }}
                  data-testid="rvp-tirage-au-sort"
                  title="Tirage au sort d'un prénom pour distribuer la parole"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-200 text-[12px] font-semibold transition"
                >
                  <Shuffle size={13} /> À qui la parole ?
                </button>
              )}
              <button
                onClick={() => setShowParticipantsModal(true)}
                data-testid="rvp-open-participants"
                title="Gérer les participants de la séance"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold transition ${participants.eleves.length > 0 ? 'border border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20' : 'border border-white/10 text-white/60 hover:border-white/30 hover:text-white'}`}
              >
                {participants.eleves.length > 0 ? <><Edit2 size={12} /> {participants.eleves.length} élève{participants.eleves.length > 1 ? 's' : ''}</> : <><Users size={13} /> Participants</>}
              </button>
            </>
          )}
          {/* QCM controls — visible uniquement sur slides QCM */}
          {isQcmSlide && (
            <>
              <button
                onClick={toggleReveal}
                data-testid="rvp-reveal-toggle"
                title="Révéler la bonne réponse à la salle (R)"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold transition ${currentRevealed ? 'border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]' : 'border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20'}`}
              >
                <Eye size={13} /> {currentRevealed ? 'Réponse révélée' : 'Révéler'}
              </button>
              <button
                onClick={resetVotes}
                data-testid="rvp-reset-votes"
                title="Effacer tous les votes de la session"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold border border-white/10 text-white/70 hover:border-red-500/50 hover:bg-red-950/25 hover:text-red-200 transition"
              >
                <Trash2 size={12} /> Reset votes
              </button>
            </>
          )}
          {/* Chrono */}
          <div className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border text-[11.5px] font-mono-spec ${running ? 'border-emerald-500/50 bg-emerald-950/25 text-emerald-300' : 'border-white/10 text-white/60'}`}>
            <Clock size={13} />
            <span data-testid="rvp-chrono">{fmt(elapsed)}</span>
            <button onClick={() => setRunning((r) => !r)} className="hover:text-white" title="Démarrer/Pause (T)">{running ? <Pause size={12} /> : <Play size={12} />}</button>
            <button onClick={() => { setElapsed(0); setRunning(false); }} className="hover:text-white" title="Reset chrono"><RotateCcw size={12} /></button>
          </div>
          {/* Ouvrir vitrine */}
          <button onClick={openPublicWindow} data-testid="rvp-open-public" title="Ouvrir la vitrine pour le vidéoprojecteur (P)" className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold transition ${publicOpen ? 'border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]' : 'border border-red-500/50 bg-red-600/15 text-red-200 hover:bg-red-600/25 hover:text-white animate-pulse'}`}>
            <Projector size={13} /> {publicOpen ? 'Vidéoprojecteur actif' : 'Mode vidéoprojecteur'}
          </button>
          {/* Cross-device cast (téléphone → écran distant) */}
          <button
            onClick={() => setShowCastModal(true)}
            data-testid="rvp-open-cast"
            title="Télécommande cross-device : téléphone contrôle un écran distant via QR / code"
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold transition ${cast.active ? 'border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]' : 'border border-fuchsia-500/50 bg-fuchsia-600/15 text-fuchsia-200 hover:bg-fuchsia-600/25 hover:text-white'}`}
          >
            <Smartphone size={13} /> {cast.active ? `Diffusion · ${cast.code}` : 'Diffuser en salle'}
          </button>
          {/* Spectateur QR — élèves scannent, voient sur leur téléphone */}
          <button
            onClick={() => setShowSpectateurModal(true)}
            data-testid="rvp-open-spectateur"
            title="Spectateur QR : les élèves scannent et suivent le cours sur leur téléphone"
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold transition ${spectateurCast.active ? 'border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]' : 'border border-emerald-500/50 bg-emerald-600/15 text-emerald-200 hover:bg-emerald-600/25 hover:text-white'}`}
          >
            <Users size={13} /> {spectateurCast.active ? `Élèves · ${spectateurCast.code}` : 'Spectateur QR'}
          </button>
          {/* Envoyer/déplacer la vitrine sur l'écran 2 + plein écran */}
          {publicOpen && (
            <>
              <button
                onClick={sendToProjector}
                data-testid="rvp-send-to-projector"
                title="Envoyer la vitrine sur le vidéoprojecteur (écran 2) et passer en plein écran"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold border border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 transition"
              >
                <Monitor size={13} /> Envoyer sur écran 2
              </button>
              <button
                onClick={swapScreens}
                data-testid="rvp-swap-screens"
                title="Inverser : envoie la vitrine sur l'autre écran (utile si les écrans sont inversés)"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold border border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 transition"
              >
                <Repeat size={13} /> Inverser les écrans
              </button>
              <button
                onClick={() => { try { bcRef.current?.postMessage({ cmd: 'showTestPattern' }); } catch { /* noop */ } }}
                data-testid="rvp-test-pattern"
                title="Afficher une mire de test sur le projecteur (résolution, couleurs, lisibilité, bords)"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold border border-fuchsia-500/50 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 transition"
              >
                <Target size={13} /> Tester config
              </button>
            </>
          )}
          {/* Bouton Rocket — rouvrir le wizard de lancement rapide */}
          <button
            onClick={() => setShowLaunchWizard(true)}
            data-testid="rvp-launch-wizard-btn"
            title="Lancement rapide (choisir dispositif salle)"
            className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25"
          >
            <Rocket size={13} />
          </button>
          {/* Notes toggle */}
          <button onClick={() => setShowNotes((s) => !s)} data-testid="rvp-notes-toggle" title="Afficher/masquer les notes (N)" className={`w-9 h-9 inline-flex items-center justify-center rounded-full border ${showNotes ? 'border-amber-500/50 bg-amber-950/25 text-amber-300' : 'border-white/10 text-white/60'} hover:text-white`}>
            <StickyNote size={13} />
          </button>
          {/* Toggle mode notes : side ⇄ focus (plein écran notes, slide en mini) */}
          {showNotes && (
            <button
              onClick={() => setNotesMode((m) => m === 'focus' ? 'side' : 'focus')}
              data-testid="rvp-notes-mode"
              title={notesMode === 'focus' ? 'Notes latérales (compact)' : 'Notes en grand (mode lecture prof)'}
              className={`w-9 h-9 inline-flex items-center justify-center rounded-full border transition ${notesMode === 'focus' ? 'border-amber-400/70 bg-amber-500/20 text-amber-200 shadow-[0_0_16px_-4px_rgba(245,158,11,0.6)]' : 'border-white/10 text-white/60 hover:text-white hover:border-white/25'}`}
            >
              {notesMode === 'focus' ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          )}
          {/* Bibliothèque vidéo — lancer une vidéo à tout moment */}
          <button
            onClick={() => setShowVideoPanel(true)}
            data-testid="rvp-video-panel-btn"
            title="Bibliothèque vidéo : lancer une vidéo pendant le cours (V)"
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-semibold transition ${slide?.type === 'video' ? 'border border-fuchsia-400/70 bg-fuchsia-500/20 text-fuchsia-200 shadow-[0_0_16px_-4px_rgba(217,70,239,0.6)]' : 'border border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-300/90 hover:bg-fuchsia-500/15 hover:text-fuchsia-200'}`}
          >
            <Film size={13} /> Vidéos
          </button>
          {/* Menu Aller à… */}
          <button
            onClick={() => setShowJumpMenu(true)}
            data-testid="rvp-jump-menu"
            title="Aller à un slide/section/vidéo (G)"
            className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25"
          >
            <List size={14} />
          </button>
          <button onClick={() => setShowHelp((s) => !s)} title="Raccourcis clavier (?)" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25">
            <KeyRound size={14} />
          </button>
          <button onClick={toggleFullscreen} data-testid="rvp-fullscreen" title="Plein écran (F)" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/25">
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </header>

      {/* Main area — grid presenter (mode side : 1fr / 480px | mode focus : 320px / 1fr) */}
      <main
        className="flex-1 overflow-hidden grid"
        style={{
          gridTemplateColumns: !showNotes || isFullscreen
            ? '1fr'
            : notesMode === 'focus'
              ? '340px 1fr'
              : '1fr 480px',
        }}
      >
        <div
          className="relative overflow-hidden"
          onPointerDown={cast.active ? (e) => {
            const r = e.currentTarget.getBoundingClientRect();
            cast.pushLaser({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, visible: true });
          } : undefined}
          onPointerMove={cast.active ? (e) => {
            if (e.buttons === 0 && e.pointerType === 'mouse') return; // seul drag/touch envoie
            const r = e.currentTarget.getBoundingClientRect();
            cast.pushLaser({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, visible: true });
          } : undefined}
          onPointerUp={cast.active ? () => cast.pushLaser({ x: 0.5, y: 0.5, visible: false }) : undefined}
          onPointerCancel={cast.active ? () => cast.pushLaser({ x: 0.5, y: 0.5, visible: false }) : undefined}
          data-testid="rvp-slide-area"
        >
          {SlideComponent ? <SlideComponent slide={slide} key={current} pollContext={pollContext} forceReveal={currentRevealed} /> : <div className="text-white/50 h-full flex items-center justify-center">Type de slide non supporté : {slide?.type}</div>}
          {/* Badge officiel formation (AAC / AM) sur les slides obligatoires */}
          {formationType && isOfficialSlide && (
            <div className="absolute bottom-4 right-4 pointer-events-none z-10" data-testid={`badge-formation-${formationType}`}>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-md border shadow-lg ${
                formationType === 'AAC'
                  ? 'border-red-500/50 bg-red-950/60 text-red-200'
                  : 'border-sky-500/50 bg-sky-950/60 text-sky-200'
              }`}>
                <ShieldCheck size={11} />
                <span className="text-[10px] font-bold font-mono-spec uppercase tracking-[0.2em]">
                  {formationType} · Officiel
                </span>
              </div>
            </div>
          )}
        </div>
        {showNotes && !isFullscreen && (
          <aside
            className="border-l border-white/10 bg-[#0d0d0e] overflow-y-auto p-4 sm:p-5 flex flex-col gap-4"
            data-testid="rvp-notes-panel"
          >
            {/* Notes formateur — police adaptative selon le mode */}
            <div className={`rounded-2xl border border-amber-500/25 bg-amber-950/10 ${notesMode === 'focus' ? 'p-6 sm:p-8 flex-1' : 'p-4'}`}>
              <div className={`text-amber-300 uppercase tracking-wider font-bold font-mono-spec inline-flex items-center gap-1.5 mb-3 ${notesMode === 'focus' ? 'text-[13px]' : 'text-[10.5px]'}`}>
                <StickyNote size={notesMode === 'focus' ? 14 : 11} /> Notes formateur · Slide {current + 1}/{total}
              </div>
              <div
                className={`text-white/90 whitespace-pre-wrap ${notesMode === 'focus'
                  ? 'text-[20px] sm:text-[22px] leading-[1.55] font-serif-editorial'
                  : 'text-[16px] leading-[1.6]'}`}
                style={notesMode === 'focus' ? { minHeight: '50vh' } : { minHeight: '110px' }}
              >
                {slide?.notes || <span className="text-white/40 italic">Pas de notes pour ce slide. Utilise ce moment pour interagir librement avec la salle.</span>}
              </div>
            </div>
            {/* Preview slide suivante — masquée en mode focus pour laisser place aux notes */}
            {notesMode !== 'focus' && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="text-white/50 text-[10.5px] uppercase tracking-wider font-bold font-mono-spec mb-2 inline-flex items-center gap-1.5"><ChevronDown size={11} /> Slide suivante · {current + 2} / {total}</div>
                {nextSlide ? (
                  <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0a0a0b] relative" style={{ aspectRatio: '16/10' }}>
                    <div className="absolute inset-0 scale-[0.35] origin-top-left" style={{ width: '285.7%', height: '285.7%' }}>
                      {NextComponent && <NextComponent slide={nextSlide} />}
                    </div>
                  </div>
                ) : (
                  <div className="text-white/40 text-[12px] italic">Dernier slide.</div>
                )}
              </div>
            )}
            {/* Slide info — compact toujours visible */}
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-[12px] text-white/60">
              <div className="font-mono-spec text-white/50 uppercase tracking-wider text-[10.5px] mb-1">Slide actuel</div>
              <div>Type : <span className="text-white/80 font-mono-spec">{slide?.type}</span></div>
              {slide?.kicker && <div className="mt-1">Kicker : <span className="text-white/80">{slide.kicker}</span></div>}
            </div>
          </aside>
        )}
      </main>

      {/* Bottom nav */}
      <footer className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-white/5">
        <button onClick={goPrev} disabled={current === 0} data-testid="rvp-prev" className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/10 text-white/80 hover:border-white/25 hover:text-white text-[13px] font-semibold disabled:opacity-30 disabled:cursor-not-allowed">
          <ArrowLeft size={14} /> Précédent
        </button>
        <div className="hidden sm:flex items-center gap-1">
          {slides.slice(0, Math.min(total, 20)).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all ${i === current ? 'w-8 bg-red-500' : 'w-2 bg-white/15 hover:bg-white/30'}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
          {total > 20 && <span className="text-white/30 text-[11px] font-mono-spec ml-1">+{total - 20}</span>}
        </div>
        <button onClick={goNext} disabled={current >= total - 1} data-testid="rvp-next" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold disabled:opacity-30 disabled:cursor-not-allowed">
          Suivant <ArrowRight size={14} />
        </button>
      </footer>

      {/* Modal Bibliothèque vidéo */}
      {showVideoPanel && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={() => setShowVideoPanel(false)} data-testid="rvp-video-modal">
          <div className="max-w-2xl w-full rounded-2xl border border-fuchsia-500/25 bg-[#0d0d0e] p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-fuchsia-400 font-mono-spec text-[11.5px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5"><Film size={12} /> Bibliothèque vidéo</div>
            <h3 className="mt-2 font-serif-editorial text-white text-[22px]">Lancer une vidéo pendant le cours</h3>
            <p className="mt-1 text-white/50 text-[12px]">La vidéo s&apos;affiche immédiatement sur l&apos;écran 2. Un bouton « Reprendre le cours » te ramènera exactement où tu en étais.</p>
            <div className="mt-4 space-y-2">
              {videoMarkers.map((v, n) => (
                <button
                  key={v.i}
                  onClick={() => launchVideo(v.i)}
                  data-testid={`launch-video-${n}`}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-start gap-3 ${v.i === current ? 'border-fuchsia-400/70 bg-fuchsia-500/15' : 'border-white/5 bg-white/[0.02] hover:bg-fuchsia-500/10 hover:border-fuchsia-500/40'}`}
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 flex items-center justify-center">
                    <Play size={14} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-[13.5px] font-semibold">{v.title}{v.i === current && <span className="ml-2 text-fuchsia-300 text-[10.5px] font-mono-spec uppercase">· en cours</span>}</div>
                    {v.note && <div className="text-white/50 text-[12px] leading-snug mt-0.5 line-clamp-2">{v.note}</div>}
                  </div>
                </button>
              ))}
              {videoMarkers.length === 0 && <div className="text-white/40 text-[13px] italic">Aucune vidéo disponible dans ce cours.</div>}
            </div>
            <button onClick={() => setShowVideoPanel(false)} className="mt-5 w-full h-10 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Fermer</button>
          </div>
        </div>
      )}

      {/* Bouton flottant "Reprendre le cours" après une vidéo lancée depuis la bibliothèque */}
      {videoReturnIdx !== null && slide?.type === 'video' && (
        <button
          onClick={() => { const idx = videoReturnIdx; setVideoReturnIdx(null); setCurrent(idx); }}
          data-testid="rvp-resume-course"
          className="fixed bottom-16 right-4 z-[150] inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] font-bold shadow-lg shadow-emerald-900/50 transition"
        >
          <CornerUpLeft size={14} /> Reprendre le cours (slide {videoReturnIdx + 1})
        </button>
      )}

      {/* Jump menu modal */}
      {showJumpMenu && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-start justify-center p-4 pt-16 overflow-y-auto" onClick={() => setShowJumpMenu(false)} data-testid="rvp-jump-modal">
          <div className="max-w-2xl w-full rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-red-400 font-mono-spec text-[11.5px] uppercase tracking-wider font-bold">Aller à…</div>
            <h3 className="mt-2 font-serif-editorial text-white text-[22px]">Navigation rapide · {rvp.code}</h3>
            <p className="mt-1 text-white/50 text-[12px] font-mono-spec">{jumpMarkers.length} repères · {total} slides au total</p>
            <div className="mt-4 space-y-1">
              {jumpMarkers.map((m) => {
                const isActive = m.i === current;
                const colorClass = m.kind === 'video' ? 'text-fuchsia-300 border-fuchsia-500/30' : m.kind === 'title' ? 'text-sky-300 border-sky-500/30' : 'text-red-300 border-red-500/30';
                return (
                  <button
                    key={m.i}
                    onClick={() => { setCurrent(m.i); setShowJumpMenu(false); }}
                    className={`w-full text-left px-3.5 py-2.5 rounded-lg border ${isActive ? 'border-white/40 bg-white/10 text-white' : `border-white/5 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/15 ${colorClass}`} text-[13.5px] flex items-center justify-between gap-3 transition`}
                    data-testid={`jump-to-${m.i}`}
                  >
                    <span className="truncate">{m.label}</span>
                    <span className="font-mono-spec text-[11px] text-white/40 shrink-0">{m.i + 1}/{total}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowJumpMenu(false)} className="mt-5 w-full h-10 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Fermer</button>
          </div>
        </div>
      )}

      {/* Help modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
          <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#0d0d0e] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-red-400 font-mono-spec text-[11.5px] uppercase tracking-wider font-bold">Raccourcis clavier</div>
            <h3 className="mt-2 font-serif-editorial text-white text-[22px]">Navigation présentateur</h3>
            <ul className="mt-4 space-y-2 text-[13.5px] text-white/80">
              <li className="flex justify-between"><span>Slide suivant</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">→ · Espace</kbd></li>
              <li className="flex justify-between"><span>Slide précédent</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">←</kbd></li>
              <li className="flex justify-between"><span>Premier / Dernier slide</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">Home · End</kbd></li>
              <li className="flex justify-between"><span>Plein écran</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">F</kbd></li>
              <li className="flex justify-between"><span>Vitrine projecteur</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">P</kbd></li>
              <li className="flex justify-between"><span>Notes formateur</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">N</kbd></li>
              <li className="flex justify-between"><span>Menu « Aller à… »</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">G</kbd></li>
              <li className="flex justify-between"><span>Bibliothèque vidéo</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">V</kbd></li>
              <li className="flex justify-between"><span>Révéler bonne réponse QCM</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">R</kbd></li>
              <li className="flex justify-between"><span>Chrono Start/Pause</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">T</kbd></li>
              <li className="flex justify-between"><span>Quitter / Fermer plein écran</span><kbd className="px-2 py-0.5 rounded bg-white/10 text-white/70 text-[11px] font-mono-spec">Esc</kbd></li>
            </ul>
            <button onClick={() => setShowHelp(false)} className="mt-6 w-full h-10 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Fermer</button>
          </div>
        </div>
      )}

      {/* PRO Mode onboarding overlay */}
      {showProOnboard && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[70] flex items-center justify-center p-4" data-testid="rvp-pro-onboard">
          <div className="max-w-lg w-full rounded-2xl border border-red-500/40 bg-[#0d0d0e] p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 opacity-10 pointer-events-none">
              <Projector size={200} strokeWidth={1} />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                <Sparkles size={11} /> Mode PRO Vidéoprojection
              </div>
              <h3 className="mt-4 font-serif-editorial text-white text-[26px] leading-tight">Prêt à projeter ?</h3>
              <p className="mt-2 text-white/70 text-[14px] leading-relaxed">
                En 3 étapes, ta présentation apparaît en plein écran sur le vidéoprojecteur, pendant que tu gardes le contrôle sur ton PC.
              </p>
              <ol className="mt-5 space-y-3 text-[13.5px] text-white/85">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[11px] flex items-center justify-center shrink-0 font-bold">1</span>
                  <span>Clique sur <span className="text-red-300 font-semibold">« Ouvrir la vitrine vidéoprojecteur »</span> ci-dessous — une 2ᵉ fenêtre s&apos;ouvre.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[11px] flex items-center justify-center shrink-0 font-bold">2</span>
                  <span>Glisse cette 2ᵉ fenêtre sur l&apos;écran du vidéoprojecteur, puis mets-la en plein écran (touche <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10.5px] font-mono-spec">F</kbd>).</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 border border-red-500/50 text-red-300 font-mono-spec text-[11px] flex items-center justify-center shrink-0 font-bold">3</span>
                  <span>Reviens sur ton PC. Tu vois tes <span className="text-amber-300">notes formateur</span>, la salle voit uniquement les slides. Navigue avec <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10.5px] font-mono-spec">→</kbd>.</span>
                </li>
              </ol>
              <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={() => { openPublicWindow(); setShowProOnboard(false); setShowNotes(true); }}
                  data-testid="rvp-pro-open-vitrine"
                  className="flex-1 h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition"
                >
                  <Projector size={15} /> Ouvrir la vitrine vidéoprojecteur
                </button>
                <button
                  onClick={() => setShowProOnboard(false)}
                  className="h-12 px-5 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/30 text-[13px] font-semibold"
                >
                  Plus tard
                </button>
              </div>
              <p className="mt-4 text-[11.5px] text-white/40 font-mono-spec text-center">
                Si aucune fenêtre ne s&apos;ouvre : autorise les popups pour codevoiture.fr dans ton navigateur.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal Participants */}
      {supportsParticipants && (
        <ParticipantsSetupModal
          slug={slug}
          initialData={participants}
          open={showParticipantsModal}
          onClose={() => {
            setShowParticipantsModal(false);
            sessionStorage.setItem(`rvp-participants-dismissed-${slug}`, '1');
          }}
          onSave={(next) => {
            updateParticipants(next);
            setShowParticipantsModal(false);
            sessionStorage.setItem(`rvp-participants-dismissed-${slug}`, '1');
            // Retourner au début pour montrer la slide bienvenue personnalisée
            if (next.eleves.length > 0) setCurrent(0);
          }}
        />
      )}

      {/* Launch Wizard "1 clic" — préconfigure la salle */}
      <RvpLaunchWizard
        open={showLaunchWizard}
        slug={slug}
        rvpTitle={rvp?.title}
        onClose={() => setShowLaunchWizard(false)}
        onLaunchProjector={openPublicWindow}
        onLaunchSolo={() => { /* rien de spécial */ }}
        onLaunchCast={() => setShowCastModal(true)}
        onLaunchSpectateur={() => setShowSpectateurModal(true)}
      />

      {/* Overlay Tirage au sort — flash plein écran 3s */}
      {tirageResult && (
        <div
          key={tirageResult.ts}
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md flex items-center justify-center pointer-events-none"
          data-testid="tirage-overlay"
        >
          <div className="text-center px-6 animate-in fade-in zoom-in duration-300">
            <div className="text-red-400 text-[12px] font-mono-spec uppercase tracking-[0.3em] font-bold mb-4">À qui la parole ?</div>
            <div className="font-serif-editorial text-white text-[80px] sm:text-[140px] lg:text-[180px] leading-none tracking-tight">
              {tirageResult.prenom}
            </div>
            <div className="mt-6 text-white/60 text-[15px] sm:text-[18px]">
              Tu as la parole 🎤
            </div>
          </div>
        </div>
      )}

      {/* Modal télécommande cross-device */}
      <CrossDeviceCastModal
        open={showCastModal}
        code={cast.code}
        loading={castStarting}
        error={castError || cast.error}
        onStart={startCast}
        onStopCast={stopCast}
        onClose={() => setShowCastModal(false)}
      />

      {/* Modal Spectateur QR — élèves suivent sur leur téléphone */}
      <SpectateurQrModal
        open={showSpectateurModal}
        code={spectateurCast.code}
        loading={spectateurStarting}
        error={spectateurError || spectateurCast.error}
        onStart={startSpectateur}
        onStopCast={stopSpectateur}
        onClose={() => setShowSpectateurModal(false)}
      />

      {/* Modal d'aide "Inverser les écrans" — affiché si moveTo est bloqué par le navigateur */}
      {showSwapHelp && (
        <div className="fixed inset-0 z-[9998] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowSwapHelp(false)} data-testid="swap-help-modal">
          <div className="w-full max-w-lg rounded-3xl border border-amber-500/40 bg-gradient-to-br from-[#161009] to-[#0a0a0b] p-6 sm:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                <Repeat size={11} /> Inverser les écrans
              </div>
              <button onClick={() => setShowSwapHelp(false)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
                <X size={16} />
              </button>
            </div>

            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight mb-2">
              Ton navigateur bloque le déplacement automatique
            </h2>
            <p className="text-white/60 text-[13.5px] leading-relaxed mb-5">
              C&apos;est une sécurité récente de Chrome / Firefox / Safari. Voici <strong className="text-white">2 solutions</strong> qui marchent à coup sûr :
            </p>

            {/* Solution 1 — Raccourci clavier */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-3">
              <div className="text-[10.5px] uppercase tracking-widest text-emerald-400 font-bold mb-2">Solution 1 · Le plus rapide (Windows)</div>
              <ol className="space-y-2 text-white/85 text-[13.5px]">
                <li className="flex gap-2"><span className="text-emerald-400 font-bold">1.</span> Clique sur la fenêtre <strong>vidéoprojecteur</strong> pour la sélectionner</li>
                <li className="flex gap-2"><span className="text-emerald-400 font-bold">2.</span> Appuie sur <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11.5px] font-mono-spec">Windows</kbd> + <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11.5px] font-mono-spec">Maj</kbd> + <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11.5px] font-mono-spec">←</kbd> ou <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11.5px] font-mono-spec">→</kbd></li>
                <li className="flex gap-2"><span className="text-emerald-400 font-bold">3.</span> La fenêtre bascule sur l&apos;autre écran. Appuie sur <kbd className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-[11.5px] font-mono-spec">F11</kbd> pour plein écran</li>
              </ol>
              <div className="mt-3 pt-3 border-t border-white/10 text-[12px] text-white/50">
                Sur <strong>Mac</strong> : glisse la fenêtre à la souris entre les écrans, puis <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10.5px] font-mono-spec">Ctrl + Cmd + F</kbd>
              </div>
            </div>

            {/* Solution 2 — Fermer + rouvrir */}
            <div className="rounded-2xl border border-red-500/30 bg-red-950/15 p-4 mb-5">
              <div className="text-[10.5px] uppercase tracking-widest text-red-400 font-bold mb-2">Solution 2 · Recommencer proprement</div>
              <p className="text-white/80 text-[13.5px] leading-relaxed mb-3">
                Je ferme la vitrine et j&apos;en rouvre une nouvelle. Ta fenêtre présentateur devrait rester ici, et le navigateur choisira l&apos;autre écran pour la nouvelle vitrine.
              </p>
              <button
                onClick={reopenPublicWindow}
                data-testid="swap-help-reopen"
                className="w-full h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition"
              >
                <Repeat size={14} /> Fermer et rouvrir la vitrine
              </button>
            </div>

            {/* Astuce Chrome multi-écrans */}
            <div className="rounded-xl bg-sky-950/25 border border-sky-500/30 p-3 text-[12.5px] text-sky-200 leading-relaxed">
              <strong>💡 Astuce Chrome/Edge</strong> — la prochaine fois, quand le navigateur te demande <em>« Autoriser l&apos;accès à d&apos;autres écrans ? »</em>, clique <strong>« Autoriser »</strong>. Sans ça, le déplacement auto est bloqué.
            </div>

            <button
              onClick={() => setShowSwapHelp(false)}
              className="mt-4 w-full text-white/50 hover:text-white/80 text-[12.5px] underline"
            >
              Fermer cette aide
            </button>
          </div>
        </div>
      )}

      {/* Contrôle taille du texte écran 2 + badge diffusion */}
      {(cast.active || publicWin) && (
        <div
          className="fixed bottom-16 left-4 z-[150] inline-flex items-center gap-1 px-2 h-10 rounded-full border border-white/15 bg-black/75 backdrop-blur-md shadow-lg"
          data-testid="rvp-zoom-controls"
          title="Grossir / réduire le texte affiché sur l'écran 2"
        >
          <button
            onClick={() => setTextZoom((z) => Math.max(1, +(z - 0.15).toFixed(2)))}
            data-testid="rvp-zoom-out"
            className="w-9 h-9 rounded-full text-white/80 hover:bg-white/10 active:bg-white/20 font-bold text-[12px] transition"
          >A−</button>
          <span className="text-white/60 text-[11px] font-mono-spec w-11 text-center select-none" data-testid="rvp-zoom-value">{Math.round(textZoom * 100)}%</span>
          <button
            onClick={() => setTextZoom((z) => Math.min(1.75, +(z + 0.15).toFixed(2)))}
            data-testid="rvp-zoom-in"
            className="w-9 h-9 rounded-full text-white/80 hover:bg-white/10 active:bg-white/20 font-bold text-[15px] transition"
          >A+</button>
        </div>
      )}

      {/* Badge flottant "Diffusion active" quand cross-cast actif et modal fermé */}
      {cast.active && !showCastModal && (
        <div
          className="fixed bottom-4 left-4 z-[150] inline-flex items-center gap-2 px-3 h-9 rounded-full border border-emerald-500/50 bg-emerald-500/15 backdrop-blur-md text-emerald-200 text-[11.5px] font-mono-spec uppercase tracking-wider font-bold cursor-pointer hover:bg-emerald-500/25"
          onClick={() => setShowCastModal(true)}
          data-testid="cast-active-badge"
        >
          <Radio size={12} className="animate-pulse" /> Diffusion · {cast.code}
        </div>
      )}
    </div>
  );
}
